import type { ItemPatch } from './api';

/* CSV import/export for a round.

   Columns: verdict,headline,source_name,source_url,image_hint
   Header row required. Column order does not matter and header spelling is forgiving, because
   this file comes out of a spreadsheet a human typed.

   A CSV cannot carry image bytes. The `image_hint` cell holds a filename, and the round editor
   matches a dropped folder of images against those hints afterwards. */

export const CSV_COLUMNS = ['verdict', 'headline', 'source_name', 'source_url', 'published', 'image_hint'] as const;

/** RFC 4180 enough for real headlines: quoted fields, embedded commas and quotes, CRLF, BOM. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const s = text.replace(/^﻿/, '');

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (c !== '\r') field += c;
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
}

export function toCsv(rows: (string | number | null | undefined)[][]): string {
  const escape = (v: string | number | null | undefined) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(escape).join(',')).join('\r\n');
}

const ALIASES: Record<string, string[]> = {
  verdict: ['verdict', 'real_or_fake', 'realorfake', 'type', 'answer'],
  headline: ['headline', 'title', 'text', 'story'],
  source_name: ['source_name', 'source', 'outlet', 'publisher'],
  source_url: ['source_url', 'url', 'link', 'source_link'],
  // A year or a full date. `year` is listed because that is what a hand-made research
  // spreadsheet will almost always call it.
  published: ['published', 'published_at', 'date', 'year', 'published_date', 'article_date'],
  image_hint: ['image_hint', 'graphic', 'image', 'filename', 'file'],
};

function indexHeaders(header: string[]): Record<string, number> {
  const normalised = header.map((h) => h.trim().toLowerCase().replace(/[\s-]+/g, '_'));
  const out: Record<string, number> = {};
  for (const [canonical, aliases] of Object.entries(ALIASES)) {
    const idx = normalised.findIndex((h) => aliases.includes(h));
    if (idx !== -1) out[canonical] = idx;
  }
  return out;
}

export interface CsvParseResult {
  items: ItemPatch[];
  /** Rows that could not be read, reported rather than dropped in silence. */
  rejected: { row: number; reason: string }[];
}

export function csvToItems(text: string): CsvParseResult {
  const rows = parseCsv(text);
  if (rows.length < 2) {
    throw new Error('That CSV needs a header row plus at least one headline.');
  }
  const cols = indexHeaders(rows[0]);
  const missing = ['verdict', 'headline'].filter((c) => cols[c] === undefined);
  if (missing.length) {
    throw new Error(`Missing required column(s): ${missing.join(', ')}. Expected: ${CSV_COLUMNS.join(', ')}`);
  }

  const items: ItemPatch[] = [];
  const rejected: { row: number; reason: string }[] = [];

  rows.slice(1).forEach((row, i) => {
    const cell = (name: string) => (cols[name] !== undefined ? (row[cols[name]] || '').trim() : '');
    const rowNumber = i + 2; // 1-indexed, plus the header

    const verdictRaw = cell('verdict').toLowerCase();
    const verdict = ['real', 'r', 'true', 'yes'].includes(verdictRaw)
      ? 'real'
      : ['fake', 'f', 'false', 'no'].includes(verdictRaw)
        ? 'fake'
        : null;
    if (!verdict) {
      rejected.push({ row: rowNumber, reason: `Verdict "${cell('verdict')}" is not REAL or FAKE.` });
      return;
    }
    const headline = cell('headline');
    if (!headline) {
      rejected.push({ row: rowNumber, reason: 'No headline text.' });
      return;
    }

    items.push(
      verdict === 'real'
        ? {
            verdict, headline,
            sourceName: cell('source_name'),
            sourceUrl: cell('source_url'),
            publishedAt: cell('published'),
          }
        : { verdict, headline, imageHint: cell('image_hint') },
    );
  });

  return { items, rejected };
}

export function itemsToCsv(items: { verdict: string; headline: string; sourceName: string; sourceUrl: string; publishedAt: string; imageHint: string; images: { name: string }[] }[]): string {
  return toCsv([
    [...CSV_COLUMNS],
    ...items.map((i) => [
      i.verdict.toUpperCase(),
      i.headline,
      i.verdict === 'real' ? i.sourceName : '',
      i.verdict === 'real' ? i.sourceUrl : '',
      i.verdict === 'real' ? i.publishedAt : '',
      i.verdict === 'fake' ? i.imageHint || i.images[0]?.name || '' : '',
    ]),
  ]);
}

export const CSV_TEMPLATE = toCsv([
  [...CSV_COLUMNS],
  ['REAL', 'A real headline goes here', 'BBC', 'https://www.bbc.com/news/example', '1998', ''],
  ['FAKE', 'A fabricated headline goes here', '', '', '', 'my-graphic.jpg'],
]);
